import {useEffect, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import {X} from "lucide-react";
import {PhoneHQIconAvatar} from 'ch3-ui-lib';
import {getFormatedPhoneNumber} from "../../utilis/msisdnUtilis";
import {ConfirmDialog} from "../../components/ConfirmDialog/ConfirmDialog";
import {PhoneNumberInfo} from "./PhoneNumberInfo";
import WorkingHours from "./WorkingHours";
import CallRules from "./CallRules";
import useMediaQuery from "../../utilis/screenWidthUtils.ts";
import listEmpty from "../../assets/list-empty.svg";
import appStore from "../../assets/app-store-icon.png";
import googlePlay from "../../assets/google-play-icon.png";
import {useFetchMsisdnSettingsInfoQuery} from "../../store/features/MsisdnSettings/msisdnSettingsService";
import AdminPanelRestrictedView from "../../components/AdminPanelRestrictedView/AdminPanelRestrictedView";
import {findPhoneSettingsItem, PHONE_SETTINGS_SECTIONS} from "./phoneSettingsNav";

interface PhoneNumberSettingsProps {
    admin: boolean;
}

// Phone number settings as the DS form panel: 640px over the lines list, a 200px
// section navigation on the left and the chosen settings beside it.
const PhoneNumberSettings = ({admin = false}: PhoneNumberSettingsProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const isAdminPath = location.pathname.includes('admin');
    const role = useSelector((state: any) => state.auth.role);
    const {id} = useParams<{ id: string }>();
    const isMobile = useMediaQuery('(max-width: 960px)');
    const {data: msisdn} = useFetchMsisdnSettingsInfoQuery(id as string);
    const [activeId, setActiveId] = useState('info');
    const active = findPhoneSettingsItem(activeId);

    const leave = () => navigate(admin ? '/admin/phoneNumbers' : '/phoneNumbers');
    // SettingsPhone-Unsaved: edits in Information are dropped on close or on switching section, so ask first.
    const [infoDirty, setInfoDirty] = useState(false);
    const [pending, setPending] = useState<null | (() => void)>(null);
    const guard = (action: () => void) => (infoDirty ? setPending(() => action) : action());
    const close = () => guard(leave);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            // A dialog on top (e.g. the welcome message editor) handles its own Escape and marks it handled.
            if (e.key === 'Escape' && !e.defaultPrevented && !pending) close();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    });

    // Call rule items share one view; bring the chosen section into sight.
    useEffect(() => {
        if (!active.anchor) return;
        const frame = window.requestAnimationFrame(() => {
            document.getElementById(active.anchor!)?.scrollIntoView({block: 'start', behavior: 'smooth'});
        });
        return () => window.cancelAnimationFrame(frame);
    }, [active.anchor, activeId]);

    if (Boolean(role == 'USER') && isAdminPath) {
        return <AdminPanelRestrictedView/>;
    }

    if (isMobile) {
        return (
            <div className='m-8 flex justify-center rounded-card bg-surface text-center'>
                <div className='m-14 w-[450px]'>
                    <div className='flex w-full justify-center'><img className='m-4' src={listEmpty} alt=""/></div>
                    <h2 className='mb-6 mt-2 text-title text-ink'>Please visit our Mobile App in order to access Phone Number Settings</h2>
                    <div className="flex flex-wrap items-center justify-center">
                        <a href="https://apps.apple.com/us/app/phonehq/id6503608602"><img className='m-4' width="138" src={appStore} alt="App Store"/></a>
                        <a href="https://play.google.com/store/apps/details?id=com.phonehq"><img className='m-4' width="138" src={googlePlay} alt="Google Play"/></a>
                    </div>
                </div>
            </div>
        );
    }

    const label = msisdn?.msisdn?.label || 'Phone number';

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-scrim" onClick={close} data-testid="phone-settings-scrim"/>
            <aside role="dialog" aria-label={label} className="relative flex h-full w-[800px] max-w-[90vw] flex-col bg-surface shadow-overlay">
                <div className="flex h-header shrink-0 items-center gap-3 border-0 border-b border-solid border-line px-5">
                    {/* Same tile as the line in the list: its own icon and colour. */}
                    <PhoneHQIconAvatar icon={msisdn?.msisdn?.icon || 'home'} color={msisdn?.msisdn?.iconColor || 'blue'} size="32px"/>
                    <div className="flex min-w-0 flex-1 flex-col">
                        <h2 className="m-0 truncate text-body-strong text-ink">{label}</h2>
                        {msisdn?.msisdn?.msisdn && <span className="text-footnote text-ink-muted">{getFormatedPhoneNumber(msisdn.msisdn.msisdn, true)}</span>}
                    </div>
                    <button type="button" aria-label="Close" onClick={close}
                            className="flex size-8 cursor-pointer items-center justify-center rounded-chip text-ink-secondary hover:bg-surface-muted">
                        <X size={18} aria-hidden="true" className="!p-0 !text-current"/>
                    </button>
                </div>
                <div className="flex min-h-0 flex-1">
                    <nav aria-label="Line settings" className="flex w-[200px] shrink-0 flex-col gap-0.5 overflow-y-auto px-2 pb-2">
                        {PHONE_SETTINGS_SECTIONS.map((section) => (
                            <div key={section.title} className="flex flex-col gap-0.5">
                                <div className="px-2 pb-1 pt-3 text-caption text-ink-muted">{section.title}</div>
                                {section.items.map((item) => (
                                    <button
                                        key={item.id}
                                        type="button"
                                        aria-current={item.id === activeId ? 'page' : undefined}
                                        onClick={() => item.id !== activeId && guard(() => {
                                            setInfoDirty(false);
                                            setActiveId(item.id);
                                        })}
                                        className={`flex h-9 cursor-pointer items-center rounded-control px-2 text-left text-callout ${
                                            item.id === activeId ? 'bg-brand-tint-subtle font-semibold text-brand-ink' : 'font-medium text-ink hover:bg-surface-muted'
                                        }`}
                                    >
                                        {item.label}
                                    </button>
                                ))}
                            </div>
                        ))}
                    </nav>
                    <div className="min-w-0 flex-1 overflow-y-auto border-0 border-l border-solid border-line p-5">
                        {active.view === 'info' && <PhoneNumberInfo admin={admin} onDirtyChange={setInfoDirty} onCancel={close}/>}
                        {active.view === 'hours' && <WorkingHours admin={admin}/>}
                        {active.view === 'rules' && <CallRules admin={admin}/>}
                    </div>
                </div>
            </aside>
            <ConfirmDialog
                open={pending !== null}
                onOpenChange={(open) => !open && setPending(null)}
                title="Unsaved changes"
                cancelLabel="Keep editing"
                confirmLabel="Leave"
                tone="leave"
                onConfirm={() => {
                    const action = pending;
                    setPending(null);
                    action?.();
                }}
            >
                You have unsaved changes. Leave without saving?
            </ConfirmDialog>
        </div>
    );
};
export default PhoneNumberSettings;
